import Country from './Country';
import State from './State';
import { toDisplayPrice } from '../lib/util';

/**
 * A rollup of the shipping on a {@link Project Project} or an {@link Order Order}.
 *
 * Read the two halves differently. The destination fields - country, state, postalCode - describe the
 * **first** shipment only, while price and any signature-on-delivery surcharge are totalled across
 * **every** shipment (ShippingSummaryMapper, TTI-20002). On anything shipping to more than one place
 * that combination is easy to misread as a single shipment's cost.
 *
 * There is no street address here; the DTO has none. For an order, use
 * {@link Collaterate.Order.getShipments Collaterate.Order.getShipments}, which returns each shipment
 * with its full ship-to address and tracking.
 *
 * This is null rather than empty when no shipping service has been selected yet.
 *
 * @property {?Country} country - (Read only) The destination country for this shipment.
 * @property {?State} state - (Read only) The desination state for this shipment.
 * @property {?string} postalCode - (Read only) The destination postal code for this shipment.
 * @property {string} description - (Read only) The label for this shipping service.
 * @property {string} vendorName - (Read only) The name of this shipping service vendor.
 * @property {number} price - (Read only) The price of shipping.
 * @property {string} displayPrice - (Read only) this.price as string, formatted with commas and two decimal places.
 * @property {?number} daysInTransit - (Read only) The number of days in transit for this shipping service. Null if not applicable (e.g. Chanhassen Pick Up).
 * @property {number} signatureOnDeliverySurcharge - (Read only) Signature on delivery surcharge included in this.price, totalled across every shipment. 0 when none applies. Signature is requested per shipment - see {@link OrderShipment#isSignatureOnDelivery OrderShipment#isSignatureOnDelivery}.
 * @property {string} displaySignatureOnDeliverySurcharge - (Read only) this.signatureOnDeliverySurcharge formatted with commas and two decimal places.
 */

class ShippingSummary {
    constructor (data) {
        this.country = data.country ? new Country(data.country) : null;
        this.state = data.state ? new State(data.state) : null;
        this.postalCode = data.postalCode || null;
        this.description = data.shippingServiceDescription;
        this.vendorName = data.shippingVendorName;
        this.price = data.price || 0;
        this.displayPrice = toDisplayPrice(this.price);
        this.daysInTransit = data.daysInTransit;

        /*
         * Priced per package and therefore summed across shipments, like this.price - the API leaves it
         * null when no surcharge applies, which reads as 0 here.
         *
         * shippingServiceSelected is the one field of this payload still unmapped: the summary is only
         * built at all when a service description resolves, so what it adds over that is not yet clear
         * enough to document honestly.
         */
        this.signatureOnDeliverySurcharge = data.signatureOnDeliverySurcharge || 0;
        this.displaySignatureOnDeliverySurcharge = toDisplayPrice(this.signatureOnDeliverySurcharge);
    }
}

export default ShippingSummary;
